import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { UserGroupDataQualityPromptCustomer } from '@components/UserGroupDataQuality/UserGroupDataQualityPromptCustomer';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { ScreenBaseWrapper } from 'noddi-ui-web';
import { BookingDetailsTabs, getJobCompletedAndPaymentLeft } from 'noddi-util';
import { DepartmentOrganizationName } from 'noddi-web';
import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLocalBrandFromOrg } from '../../hooks/useLocalBrandFromOrg';
import { BSlugContent } from './BSlugContent';
import { BookingUpdateOfferSection } from './BookingUpdateOfferSection/BookingUpdateOfferSection';

export const BSlug = () => {
  const { slug } = useParams() as { slug: string };
  const [searchParams] = useSearchParams();
  const paymentButtonRef = useRef<HTMLDivElement | null>(null);

  const {
    data: booking,
    isPending: isBookingPending,
    isFetching: isBookingFetching,
    error: bookingError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug }
  });

  // Localhost has no brand hostname, so adopt the organization brand the
  // same way a proposal does. The prompt below uses the booking's own brand.
  useLocalBrandFromOrg({ serviceOrganizationId: booking?.serviceOrganizationId });

  const isStationary = booking?.locationType === BookingLocationType.STATIONARY;
  const currentTab = searchParams.get('tab') as BookingDetailsTabs | null;

  const defaultTab =
    isStationary && (!currentTab || currentTab === BookingDetailsTabs.STATUS_UPDATES)
      ? BookingDetailsTabs.ORDER_DETAILS
      : (currentTab ?? undefined);

  // Auto-scroll to payment button when component mounts and payment is required
  useEffect(() => {
    if (!booking) {
      return;
    }

    const shouldScroll = getJobCompletedAndPaymentLeft(booking);
    if (!shouldScroll || !paymentButtonRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      paymentButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [booking]);

  // Payment callback will change status on booking so make sure to also listen to fetching here
  if (isBookingPending || isBookingFetching) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingError) {
    return <ApiErrorPageWithTrans error={[bookingError]} />;
  }

  const isOrganization = !booking.userGroup.isPersonal;

  return (
    <CustomerDataQualityProvider
      userGroupId={booking.userGroup.id}
      bookingLocationType={booking.locationType}
      brandDomain={booking.serviceDepartmentBrand.domain}
      countryCode={booking.address?.countryCode}
      // Bookings made by the shop never pass our checkout; this page
      // is often the only time the customer can fix the invoice email/address.
      openWhenBlocked
    >
      <ScreenBaseWrapper width='lg'>
        <div className='flex flex-col gap-5'>
          <div className='flex flex-col gap-2 text-balance'>
            <h1 className='text-3xl font-bold tracking-tight text-primary md:text-4xl'>
              {t`Hi`} {booking.user.firstName}!
            </h1>
            {isOrganization && (
              <p className='flex items-start gap-1.5 text-sm text-muted-foreground'>
                <NoddiIcon name='Building2' size='small' />
                <span>
                  <Trans>On behalf of</Trans>
                </span>
                {booking.customerDepartmentName ? (
                  <DepartmentOrganizationName
                    departmentName={booking.customerDepartmentName}
                    organizationName={booking.customerOrganizationName}
                  />
                ) : (
                  booking.userGroup.name
                )}
              </p>
            )}
          </div>
          <UserGroupDataQualityPromptCustomer />
          <BookingUpdateOfferSection booking={booking} />
          <BSlugContent booking={booking} defaultTab={defaultTab} paymentButtonRef={paymentButtonRef} />
        </div>
      </ScreenBaseWrapper>
    </CustomerDataQualityProvider>
  );
};
